/**
 * EmailSettings — Settings tab for transactional email infrastructure.
 *
 * Shows the active provider, available templates, lets creators send a test
 * email and reviews the audit log of every transactional send.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Mail, Send, RefreshCw, CheckCircle2, AlertCircle, Megaphone, Ban } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function EmailSettings() {
  const [provider, setProvider] = useState(null);
  const [templates, setTemplates] = useState({});
  const [selected, setSelected] = useState("welcome");
  const [recipient, setRecipient] = useState("");
  const [log, setLog] = useState([]);
  const [sending, setSending] = useState(false);

  const refreshAll = useCallback(async () => {
    try {
      const [p, t, l] = await Promise.all([
        api.get("/email/provider"),
        api.get("/email/templates"),
        api.get("/email/log?limit=50"),
      ]);
      setProvider(p.data?.provider);
      setTemplates(t.data || {});
      setLog(l.data || []);
    } catch (e) {
      // soft-fail; preview environment may not have email yet
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const sendTest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/email/test", {
        to: recipient || undefined,
        template_id: selected,
      });
      if (r.data?.ok) {
        toast.success(`Sent via ${r.data.provider}${r.data.delivered ? "" : " (logged — set RESEND_API_KEY to deliver)"}`);
      } else {
        toast.error(r.data?.error || "Failed to send");
      }
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send test email");
    }
    setSending(false);
  };

  const reloadProvider = async () => {
    try {
      const r = await api.post("/email/provider/reload");
      setProvider(r.data?.provider);
      toast.success(`Provider reloaded: ${r.data?.provider}`);
    } catch {
      toast.error("Reload failed");
    }
  };

  const providerLive = provider === "resend";
  const tplKeys = Object.keys(templates);

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="settings-email">
      <div className="max-w-3xl space-y-6">
        {/* Quick links */}
        <div className="grid sm:grid-cols-2 gap-3" data-testid="email-quick-links">
          <Link
            to="/email/campaigns"
            data-testid="email-campaigns-link"
            className="surface p-4 hover:shadow-md transition flex items-center gap-3 group"
          >
            <div className="rounded-xl bg-violet-50 p-2.5 text-ai">
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-ink">Campaigns</p>
              <p className="text-xs text-meta">Compose · target · send broadcasts</p>
            </div>
            <span className="text-meta-soft text-xs group-hover:text-ink transition">→</span>
          </Link>
          <UnsubscribesCard />
        </div>

        {/* Provider status */}
        <div className="surface p-6" data-testid="email-provider-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2 mb-1">
                <Mail className="h-4 w-4 text-ai" /> Transactional email
              </h3>
              <p className="text-sm text-meta">
                Welcome, OTP, purchase confirmations, abandoned-cart recovery and campaign sends.
              </p>
            </div>
            <button
              type="button"
              onClick={reloadProvider}
              data-testid="email-reload-provider-btn"
              className="rounded-full border border-line px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-white inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Reload
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-line bg-white px-4 py-3">
            {providerLive ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            <div className="flex-1">
              <p className="text-sm font-semibold text-ink" data-testid="email-active-provider">
                Active provider: {provider || "—"}
              </p>
              <p className="text-xs text-meta">
                {providerLive
                  ? "Live delivery enabled. All transactional sends route through Resend."
                  : "Logging mode — emails are persisted to the audit log only. Set RESEND_API_KEY in env to enable live delivery."}
              </p>
            </div>
          </div>
        </div>

        {/* Send test */}
        <form onSubmit={sendTest} className="surface p-6" data-testid="email-test-form">
          <h3 className="font-display text-lg font-bold text-ink mb-1">Send a test</h3>
          <p className="text-sm text-meta mb-4">
            Renders the template with sample variables to verify formatting and provider delivery.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="email-template-select"
              className="rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {tplKeys.map((k) => (
                <option key={k} value={k}>{templates[k]?.subject || k}</option>
              ))}
            </select>
            <input
              type="email"
              placeholder="recipient@email.com (defaults to you)"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              data-testid="email-test-recipient-input"
              className="rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="email-send-test-btn"
            className="mt-3 rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50 transition focus-ring"
          >
            <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send test"}
          </button>
        </form>

        {/* Email log */}
        <div className="surface p-6" data-testid="email-log-card">
          <h3 className="font-display text-lg font-bold text-ink mb-1">Recent sends</h3>
          <p className="text-sm text-meta mb-4">
            Every transactional email — confirmations, welcomes, recovery — is logged here for audit.
          </p>
          {log.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line bg-white px-4 py-8 text-center">
              <p className="text-sm text-meta">No emails sent yet. Send a test above to see entries.</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="email-log-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-line text-meta text-xs uppercase tracking-wider">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Template</th>
                    <th className="py-2 pr-3">To</th>
                    <th className="py-2 pr-3">Subject</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((row) => (
                    <tr key={row.id} className="border-b border-line/60">
                      <td className="py-2 pr-3 text-xs text-meta whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-ink-soft">{row.template_id || row.purpose || "—"}</td>
                      <td className="py-2 pr-3 text-ink-soft">{(row.to || []).join(", ")}</td>
                      <td className="py-2 pr-3 text-ink truncate max-w-[280px]">{row.subject}</td>
                      <td className="py-2 pr-3">
                        {row.delivered ? (
                          <span className="pill pill-ai">delivered</span>
                        ) : (
                          <span className="pill" title={row.error || "logged"}>logged</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function UnsubscribesCard() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/email/unsubscribes?limit=200").then((r) => setCount((r.data || []).length)).catch(() => {});
  }, []);
  return (
    <div className="surface p-4 flex items-center gap-3" data-testid="email-unsub-card">
      <div className="rounded-xl bg-slate-100 p-2.5 text-slate-600">
        <Ban className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink">Suppression list</p>
        <p className="text-xs text-meta">{count} unsubscribed · workspace-wide</p>
      </div>
    </div>
  );
}
